import { useEffect, useState } from "react";
import { useAuth } from "../components/AuthGate";
import { supabase } from "../lib/supabase";
import { openDisclosurePDF } from "../lib/disclosurePDF";
import { renderMD } from "../seller/lib/markdown";
import Logo from "../seller/components/Logo";
import { ACCOUNT_CSS } from "./styles";

const fmtMoney = (cents) => cents == null ? "—" : `$${Math.round(cents / 100).toLocaleString()}`;

// Account page — one place for everything tied to this person's identity,
// regardless of which role they used it in. Every section here is a pure
// read of data that already exists (seller_listings.stages, seller_
// verifications, buyer_saved_listings, offers) — no new generation, no new
// AI calls, matches the pattern already used across the seller/buyer flows.
export default function AccountApp() {
  const auth = useAuth();
  const uid = auth?.user?.id || null;

  const [listings, setListings] = useState(null);
  const [verifications, setVerifications] = useState(null);
  const [savedListings, setSavedListings] = useState(null);
  const [offers, setOffers] = useState(null);

  useEffect(() => {
    if (!supabase || !uid) return;
    supabase.from("seller_listings").select("*").eq("client_id", uid)
      .order("updated_at", { ascending: false })
      .then(({ data }) => setListings(data || []));
    supabase.from("seller_verifications").select("*").eq("user_id", uid)
      .order("created_at", { ascending: false })
      .then(({ data }) => setVerifications(data || []));
    supabase.from("buyer_saved_listings").select("*").eq("user_id", uid)
      .order("created_at", { ascending: false })
      .then(({ data }) => setSavedListings(data || []));
    supabase.from("offers").select("*").eq("buyer_id", uid)
      .order("created_at", { ascending: false })
      .then(({ data }) => setOffers(data || []));
  }, [uid]);

  const home = () => (window.location.href = "/");

  if (!supabase) {
    return (
      <div className="ac-app">
        <style>{ACCOUNT_CSS}</style>
        <div className="ac-loading">Account requires sign-in to be configured.</div>
      </div>
    );
  }

  return (
    <div className="ac-app">
      <style>{ACCOUNT_CSS}</style>
      <header className="ac-top">
        <div className="ac-brand" onClick={home}>
          <Logo />
          <div className="ac-brand-name">6zero.ai</div>
        </div>
        <button type="button" className="ac-back" onClick={home}>home</button>
      </header>

      <main className="ac-main">
        <h1 className="ac-h1">Your account</h1>
        <p className="ac-sub">{auth?.user?.email}</p>

        <section className="ac-section">
          <div className="ac-section-title">Your listings</div>
          {listings === null && <div className="ac-loading">Loading…</div>}
          {listings?.length === 0 && (
            <div className="ac-empty">
              No listings yet.{" "}
              <button type="button" className="ac-btn" style={{ marginTop: 0, marginLeft: 6 }} onClick={() => (window.location.href = "/sell/v2")}>Sell a home →</button>
            </div>
          )}
          {listings?.map((l) => <ListingCard key={l.id} listing={l} />)}
        </section>

        <section className="ac-section">
          <div className="ac-section-title">Verification history</div>
          {verifications === null && <div className="ac-loading">Loading…</div>}
          {verifications?.length === 0 && <div className="ac-empty">No document verifications yet.</div>}
          {verifications?.map((v) => (
            <div key={v.id} className="ac-card">
              <div className="ac-card-head">
                <div>
                  <div className="ac-card-addr">{v.type === "ownership" ? "Ownership document" : "Inspection report"}</div>
                  <div className="ac-card-meta">{v.doc_filename || "—"} · {new Date(v.created_at).toLocaleDateString()}</div>
                </div>
                <span className={`ac-status ${v.status}`}>{v.status.replace("_", " ")}</span>
              </div>
            </div>
          ))}
        </section>

        <section className="ac-section">
          <div className="ac-section-title">Saved homes</div>
          {savedListings === null && <div className="ac-loading">Loading…</div>}
          {savedListings?.length === 0 && (
            <div className="ac-empty">
              Nothing saved yet.{" "}
              <button type="button" className="ac-btn" style={{ marginTop: 0, marginLeft: 6 }} onClick={() => (window.location.href = "/buy")}>Browse homes →</button>
            </div>
          )}
          {savedListings?.map((s) => (
            <div key={s.id} className="ac-card">
              <div className="ac-card-head">
                <div>
                  <div className="ac-card-addr">{s.listing?.address || s.listing_id}</div>
                  <div className="ac-card-meta">
                    {s.listing?.price ? `$${s.listing.price.toLocaleString()}` : ""} · saved {new Date(s.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </section>

        <section className="ac-section">
          <div className="ac-section-title">Your offers</div>
          {offers === null && <div className="ac-loading">Loading…</div>}
          {offers?.length === 0 && <div className="ac-empty">No offers submitted yet.</div>}
          {offers?.map((o) => (
            <div key={o.id} className="ac-card">
              <div className="ac-card-head">
                <div>
                  <div className="ac-card-addr">{o.property_address}</div>
                  <div className="ac-card-meta">{fmtMoney(o.offer_price_cents)} · {new Date(o.created_at).toLocaleDateString()}</div>
                </div>
                <span className={`ac-status ${o.status}`}>{o.status}</span>
              </div>
            </div>
          ))}
        </section>
      </main>
    </div>
  );
}

function ListingCard({ listing }) {
  const stages = listing.stages || {};
  const disclosures = stages.disclosures?.data;
  const photos = stages.photography?.data?.photos || [];
  const doneCount = Object.values(stages).filter((s) => s?.status === "done").length;

  return (
    <div className="ac-card">
      <div className="ac-card-head">
        <div>
          <div className="ac-card-addr">{listing.address || "—"}</div>
          <div className="ac-card-meta">{doneCount} of 12 steps done · updated {new Date(listing.updated_at).toLocaleDateString()}</div>
        </div>
        <button type="button" className="ac-back" style={{ marginLeft: 0 }} onClick={() => (window.location.href = "/sell")}>open</button>
      </div>

      {disclosures?.summary && (
        <div className="ac-disclosure">
          <div dangerouslySetInnerHTML={{ __html: renderMD(disclosures.summary) }} />
          <button
            type="button"
            className="ac-btn"
            onClick={() => openDisclosurePDF(listing.address, disclosures.questions || [], disclosures.answers || {}, disclosures.summary)}
          >⬇ Download disclosure PDF</button>
        </div>
      )}

      {photos.length > 0 && (
        <div className="ac-photos">
          {photos.map((url) => (
            <div key={url} className="ac-photo"><img src={url} alt="listing" /></div>
          ))}
        </div>
      )}
    </div>
  );
}
